import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Utensils, Download, Loader2, Save, ArrowLeft, Calendar as CalendarIcon,
  Baby, Milk, AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKS = [1, 2, 3, 4];
const AGE_GROUPS = [
  { key: "child",  label: "12 months + and older",  Icon: Utensils },
  { key: "infant", label: "Birth through 11 months", Icon: Milk },
];

/** Deep-set selections[age][week][slot][day] = value. */
function setSelection(prev, age, week, slot, day, value) {
  const next = { ...(prev || {}) };
  next[age] = { ...(next[age] || {}) };
  next[age][String(week)] = { ...(next[age][String(week)] || {}) };
  next[age][String(week)][slot] = { ...(next[age][String(week)][slot] || {}) };
  next[age][String(week)][slot][day] = value;
  return next;
}

function getSelection(sel, age, week, slot, day) {
  return sel?.[age]?.[String(week)]?.[slot]?.[day] ?? "";
}

export default function FoodMenu() {
  const [catalog, setCatalog] = useState(null);
  const [selections, setSelections] = useState({});
  const [allergenNotes, setAllergenNotes] = useState("");
  const [daysMode, setDaysMode] = useState("5");
  const [activeWeek, setActiveWeek] = useState(1);
  const [activeAge, setActiveAge] = useState("child");
  const [includeInfants, setIncludeInfants] = useState(true);
  const [includeChildren, setIncludeChildren] = useState(true);
  const [printMode, setPrintMode] = useState("weekly"); // "weekly" | "monthly"
  const [singleWeek, setSingleWeek] = useState(1);      // when printMode === "weekly"
  const [selectedWeeks, setSelectedWeeks] = useState([1, 2, 3, 4]); // when printMode === "monthly"
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dateOfMonth, setDateOfMonth] = useState(""); // optional posting date
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cat, prefs] = await Promise.all([
          api.get("/menus/catalog"),
          api.get("/menus/preferences"),
        ]);
        setCatalog(cat.data);
        setSelections(prefs.data.selections || {});
        setAllergenNotes(prefs.data.allergen_notes || "");
        setDaysMode(prefs.data.days_mode || cat.data.default_days_mode || "5");
      } catch (err) {
        toast.error(formatApiError(err));
      } finally { setLoading(false); }
    })();
  }, []);

  const days = useMemo(() => {
    if (!catalog) return [];
    return daysMode === "7" ? catalog.days_7 : catalog.days_5;
  }, [catalog, daysMode]);

  const currentMenu = catalog
    ? (activeAge === "infant" ? catalog.infant_menu : catalog.child_menu)
    : null;

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.put("/menus/preferences", {
        selections, allergen_notes: allergenNotes, days_mode: daysMode,
      });
      toast.success("Menu selections saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const generatePdf = async () => {
    if (!includeInfants && !includeChildren) {
      toast.error("Include at least one age group.");
      return;
    }
    const weeksToPrint = printMode === "weekly" ? [singleWeek] : selectedWeeks;
    if (weeksToPrint.length === 0) {
      toast.error("Pick at least one week to print.");
      return;
    }
    setGenBusy(true);
    try {
      const res = await api.post(
        "/menus/pdf",
        {
          month, year,
          weeks: weeksToPrint,
          days_mode: daysMode,
          include_infants: includeInfants,
          include_children: includeChildren,
          selections,
          allergen_notes: allergenNotes,
          posting_date: dateOfMonth || null,
        },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `food-menu-${year}-${String(month).padStart(2, "0")}.pdf`;
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setGenBusy(false); }
  };

  if (loading || !catalog) {
    return (
      <section className="max-w-6xl mx-auto p-6" data-testid="food-menu-page">
        <div className="py-20 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" /> Loading menu catalog…
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto p-6 space-y-8" data-testid="food-menu-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/dashboard/licensing" className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700 inline-flex items-center gap-1 hover:underline" data-testid="food-menu-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Licensing file
          </Link>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-medium text-stone-900 tracking-tight flex items-center gap-3">
            <Utensils className="w-8 h-8 text-emerald-700" /> Monthly Food Menu
          </h1>
          <p className="mt-2 text-sm text-stone-600 max-w-2xl leading-relaxed">
            Pick from the 4-week catalog (or type your own) for each day and slot,
            then print a landscape menu for the licensing binder. Includes infants
            (6-11 mo) and children (12+ mo), allergen notes, and WIC/CACFP footer.
          </p>
        </div>
      </header>

      {/* Print controls */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4" data-testid="food-menu-controls">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <Label className="text-xs">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger data-testid="food-menu-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Input
              data-testid="food-menu-year"
              type="number" min={2024} max={2032}
              value={year} onChange={(e) => setYear(parseInt(e.target.value || year, 10))}
            />
          </div>
          <div>
            <Label className="text-xs">Posting date (prints on menu)</Label>
            <Input
              data-testid="food-menu-date"
              type="date"
              value={dateOfMonth}
              onChange={(e) => {
                setDateOfMonth(e.target.value);
                if (e.target.value) {
                  const d = new Date(e.target.value);
                  if (!isNaN(d)) {
                    setMonth(d.getUTCMonth() + 1);
                    setYear(d.getUTCFullYear());
                  }
                }
              }}
            />
          </div>
          <div>
            <Label className="text-xs">Operating days</Label>
            <Select value={daysMode} onValueChange={setDaysMode}>
              <SelectTrigger data-testid="food-menu-days-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5-day (Mon-Fri)</SelectItem>
                <SelectItem value="7">7-day (Mon-Sun)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Print mode</Label>
            <div className="mt-2 flex gap-1 flex-wrap" data-testid="food-menu-mode">
              <button
                type="button"
                data-testid="food-menu-mode-weekly"
                onClick={() => setPrintMode("weekly")}
                className={`h-9 px-4 rounded-lg border text-sm font-semibold transition ${
                  printMode === "weekly"
                    ? "bg-emerald-700 border-emerald-700 text-white"
                    : "bg-white border-stone-300 text-stone-600 hover:border-emerald-700"
                }`}
              >Weekly (1 week)</button>
              <button
                type="button"
                data-testid="food-menu-mode-monthly"
                onClick={() => setPrintMode("monthly")}
                className={`h-9 px-4 rounded-lg border text-sm font-semibold transition ${
                  printMode === "monthly"
                    ? "bg-emerald-700 border-emerald-700 text-white"
                    : "bg-white border-stone-300 text-stone-600 hover:border-emerald-700"
                }`}
              >Monthly (all 4 weeks)</button>
            </div>
            {printMode === "weekly" ? (
              <div className="mt-2 flex gap-1 flex-wrap" data-testid="food-menu-week-single">
                {WEEKS.map(w => (
                  <button
                    key={w} type="button"
                    data-testid={`food-menu-single-week-${w}`}
                    onClick={() => setSingleWeek(w)}
                    className={`h-8 min-w-[3.25rem] px-3 rounded-lg border text-xs font-semibold transition ${
                      singleWeek === w
                        ? "bg-stone-900 border-stone-900 text-white"
                        : "bg-white border-stone-300 text-stone-600 hover:border-stone-900"
                    }`}
                  >W{w}</button>
                ))}
                <span className="text-[11px] text-stone-500 self-center ml-1">Only this week prints</span>
              </div>
            ) : (
              <div className="mt-2 flex gap-1 flex-wrap" data-testid="food-menu-week-multi">
                {WEEKS.map(w => {
                  const on = selectedWeeks.includes(w);
                  return (
                    <button
                      key={w} type="button"
                      data-testid={`food-menu-week-toggle-${w}`}
                      onClick={() => setSelectedWeeks(
                        on ? selectedWeeks.filter(x => x !== w)
                           : [...selectedWeeks, w].sort()
                      )}
                      className={`h-8 min-w-[3.25rem] px-3 rounded-lg border text-xs font-semibold transition ${
                        on
                          ? "bg-emerald-700 border-emerald-700 text-white"
                          : "bg-white border-stone-300 text-stone-600 hover:border-emerald-700"
                      }`}
                    >W{w}</button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={includeInfants} onCheckedChange={setIncludeInfants} data-testid="food-menu-toggle-infants" />
              <Baby className="w-4 h-4 text-teal-600" /> Infants
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={includeChildren} onCheckedChange={setIncludeChildren} data-testid="food-menu-toggle-children" />
              <Utensils className="w-4 h-4 text-emerald-700" /> Children
            </label>
          </div>
        </div>

        <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 leading-relaxed" data-testid="food-menu-layout-hint">
          <span className="font-semibold">Weekly mode</span> prints just the one week you pick (2 pages: Infants + Children).
          <span className="font-semibold"> Monthly mode</span> prints all four weeks in the rotation (8 pages). Header shows
          <span className="font-semibold"> WEEK OF MM/DD/YYYY - MM/DD/YYYY</span> on every page.
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            data-testid="food-menu-generate-pdf"
            onClick={generatePdf}
            disabled={genBusy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10"
          >
            {genBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Generate PDF
          </Button>
          <Button
            data-testid="food-menu-save"
            onClick={savePrefs} disabled={saving}
            variant="outline" className="rounded-xl h-10"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save selections
          </Button>
          <div className="text-xs text-stone-500 ml-auto flex items-center gap-1">
            <CalendarIcon className="w-3.5 h-3.5" /> Header prints: {MONTHS[month - 1]} {year}
          </div>
        </div>
      </div>

      {/* Week + age group tabs */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-stone-200 bg-white p-1" data-testid="food-menu-week-tabs">
          {WEEKS.map(w => (
            <button
              key={w} type="button"
              data-testid={`food-menu-week-tab-${w}`}
              onClick={() => setActiveWeek(w)}
              className={`px-4 h-9 rounded-lg text-sm font-semibold transition ${
                activeWeek === w
                  ? "bg-stone-900 text-white"
                  : "text-stone-500 hover:text-stone-900"
              }`}
            >Week {w}</button>
          ))}
        </div>
        <div className="inline-flex rounded-xl border border-stone-200 bg-white p-1 ml-auto" data-testid="food-menu-age-tabs">
          {AGE_GROUPS.map(({ key, label, Icon }) => (
            <button
              key={key} type="button"
              data-testid={`food-menu-age-tab-${key}`}
              onClick={() => setActiveAge(key)}
              className={`px-4 h-9 rounded-lg text-sm font-semibold transition inline-flex items-center gap-1.5 ${
                activeAge === key
                  ? (key === "infant" ? "bg-teal-600 text-white" : "bg-emerald-700 text-white")
                  : "text-stone-500 hover:text-stone-900"
              }`}
            ><Icon className="w-4 h-4" /> {label}</button>
          ))}
        </div>
      </div>

      {/* Meal grid */}
      <div className="rounded-2xl border border-stone-200 bg-white overflow-x-auto" data-testid="food-menu-grid">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-stone-900 text-white text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 w-28">Meal</th>
              {days.map(d => (
                <th key={d} className="text-left px-3 py-3">{catalog.day_labels[d]}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {catalog.slots.map(slot => (
              <tr key={slot.key} className="align-top">
                <td className="px-4 py-3 font-semibold text-stone-700 bg-stone-50">{slot.label}</td>
                {days.map(day => {
                  const preset = currentMenu?.[activeWeek]?.[slot.key]?.[day] || "";
                  const chosen = getSelection(selections, activeAge, activeWeek, slot.key, day);
                  const val = chosen !== "" ? chosen : preset;
                  return (
                    <td key={day} className="px-2 py-2">
                      <Textarea
                        data-testid={`food-menu-cell-${activeAge}-${activeWeek}-${slot.key}-${day}`}
                        rows={2}
                        value={val}
                        onChange={(e) => setSelections(
                          setSelection(selections, activeAge, activeWeek, slot.key, day, e.target.value)
                        )}
                        placeholder={preset}
                        className="text-xs leading-snug resize-y min-h-[54px]"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeAge === "infant" && (
        <div className="rounded-xl border border-teal-200 bg-teal-50 text-teal-900 text-xs px-4 py-3 leading-relaxed" data-testid="food-menu-infant-birth-note">
          <span className="font-semibold uppercase tracking-wider mr-1">Birth-5 mo:</span>
          {catalog.infant_birth_to_5_mo}
        </div>
      )}

      {/* Allergen notes */}
      <div className="rounded-2xl border border-amber-200 bg-white p-5" data-testid="food-menu-allergen-block">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="w-4 h-4" /> Allergen notes &amp; substitutions
        </div>
        <p className="text-xs text-stone-500 mt-1">
          Anything typed here prints in an amber box on the PDF. Include child names,
          allergens, and the safe substitution (e.g. &quot;Sam - dairy free - soy milk&quot;).
        </p>
        <Textarea
          data-testid="food-menu-allergen-notes"
          className="mt-3"
          rows={3}
          value={allergenNotes}
          onChange={(e) => setAllergenNotes(e.target.value)}
          placeholder="e.g., Emma: peanut allergy - all snacks are peanut-free. Liam: lactose intolerant - soy milk substitute."
        />
      </div>

      <p className="text-[11px] text-stone-500 leading-relaxed">
        {catalog.compliance_footer}
      </p>
    </section>
  );
}
